import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import API from "../api/axios";
import {
  MdError, MdWarning, MdCheckCircle,
  MdNotifications, MdFilterList,
} from "react-icons/md";

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    fetchAlerts();
    // Refresh alerts every 30 seconds
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const res = await API.get("/alerts");
      setAlerts(res.data);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = alerts.filter((a) => {
    if (filter === "ALL") return true;
    if (filter === "CRITICAL") return a.severity === "CRITICAL";
    if (filter === "WARNING") return a.severity === "WARNING";
    if (filter === "RESOLVED") return a.resolved;
    if (filter === "ACTIVE") return !a.resolved;
    return true;
  });

  const counts = {
    total: alerts.length,
    critical: alerts.filter((a) => a.severity === "CRITICAL" && !a.resolved).length,
    warning: alerts.filter((a) => a.severity === "WARNING" && !a.resolved).length,
    resolved: alerts.filter((a) => a.resolved).length,
  };

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Alerts</h2>
        <p className="text-sm text-gray-500">
          Network alerts and notifications
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
          <MdNotifications size={24} className="text-blue-500" />
          <div>
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-xl font-bold text-gray-800">{counts.total}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
          <MdError size={24} className="text-red-500" />
          <div>
            <p className="text-xs text-gray-500">Critical</p>
            <p className="text-xl font-bold text-red-600">{counts.critical}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
          <MdWarning size={24} className="text-yellow-500" />
          <div>
            <p className="text-xs text-gray-500">Warning</p>
            <p className="text-xl font-bold text-yellow-600">{counts.warning}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
          <MdCheckCircle size={24} className="text-green-500" />
          <div>
            <p className="text-xs text-gray-500">Resolved</p>
            <p className="text-xl font-bold text-green-600">{counts.resolved}</p>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <MdFilterList size={18} className="text-gray-400" />
          {["ALL", "ACTIVE", "CRITICAL", "WARNING", "RESOLVED"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                filter === f
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts List */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading alerts...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <MdNotifications size={40} className="mx-auto mb-2 text-gray-300" />
            <p>No alerts found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-6 py-3 text-left">Severity</th>
                <th className="px-6 py-3 text-left">Message</th>
                <th className="px-6 py-3 text-left">Device</th>
                <th className="px-6 py-3 text-left">Time</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Resolved At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((alert) => (
                <tr key={alert.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span
                      className={`flex items-center gap-1 text-xs font-medium ${
                        alert.severity === "CRITICAL"
                          ? "text-red-600"
                          : "text-yellow-600"
                      }`}
                    >
                      {alert.severity === "CRITICAL" ? (
                        <MdError size={16} />
                      ) : (
                        <MdWarning size={16} />
                      )}
                      {alert.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-700">{alert.message}</td>
                  <td className="px-6 py-4 text-gray-600">
                    {alert.device?.name || "—"}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(alert.createdAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        alert.resolved
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {alert.resolved ? "Resolved" : "Active"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {alert.resolvedAt
                      ? new Date(alert.resolvedAt).toLocaleString()
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};

export default Alerts;